import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Pagination,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Plus,
  Edit,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { schoolFeesApi, classroomsApi, SchoolFee, Classroom } from '../../services/schoolApi';
import ConfirmDialog from '../../components/ConfirmDialog';

const SchoolFees = () => {
  const { language } = useLanguage();
  
  const [fees, setFees] = useState<SchoolFee[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [feeToDelete, setFeeToDelete] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFee, setEditingFee] = useState<SchoolFee | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterPeriodicity, setFilterPeriodicity] = useState('');
  const [filterYear, setFilterYear] = useState('');

  const [formData, setFormData] = useState({
    label: '',
    amount: '',
    periodicity: 'mensuel' as 'unique' | 'mensuel' | 'trimestriel',
    schoolYear: '',
    currency: 'CDF',
    classroomIds: [] as string[],
  });

  const text = {
    fr: {
      title: 'Gestion des Frais Scolaires',
      description: 'Créer et gérer les frais scolaires',
      addFee: 'Ajouter un frais',
      label: 'Libellé du frais',
      periodicity: 'Périodicité',
      schoolYear: 'Année scolaire',
      currency: 'Devise',
      amount: 'Montant',
      classrooms: 'Classes concernées',
      actions: 'Actions',
      edit: 'Modifier',
      delete: 'Supprimer',
      cancel: 'Annuler',
      save: 'Enregistrer',
      editFee: 'Modifier le frais',
      deleteConfirm: 'Êtes-vous sûr de vouloir supprimer ce frais ?',
      noFees: 'Aucun frais trouvé',
      loading: 'Chargement...',
      filterPeriodicity: 'Filtrer par périodicité',
      filterYear: 'Filtrer par année',
      all: 'Tous',
      unique: 'Unique',
      mensuel: 'Mensuel',
      trimestriel: 'Trimestriel',
      active: 'Actif',
      inactive: 'Inactif',
      status: 'Statut',
    },
    en: {
      title: 'School Fees Management',
      description: 'Create and manage school fees',
      addFee: 'Add Fee',
      label: 'Fee Label',
      periodicity: 'Periodicity',
      schoolYear: 'School Year',
      currency: 'Currency',
      amount: 'Amount',
      classrooms: 'Concerned Classes',
      actions: 'Actions',
      edit: 'Edit',
      delete: 'Delete',
      cancel: 'Cancel',
      save: 'Save',
      editFee: 'Edit Fee',
      deleteConfirm: 'Are you sure you want to delete this fee?',
      noFees: 'No fees found',
      loading: 'Loading...',
      filterPeriodicity: 'Filter by periodicity',
      filterYear: 'Filter by year',
      all: 'All',
      unique: 'One-time',
      mensuel: 'Monthly',
      trimestriel: 'Quarterly',
      active: 'Active',
      inactive: 'Inactive',
      status: 'Status',
    }
  };

  const currentText = text[language as keyof typeof text];

  const periodicities = [
    { value: 'unique', label: currentText.unique },
    { value: 'mensuel', label: currentText.mensuel },
    { value: 'trimestriel', label: currentText.trimestriel },
  ];

  const schoolYears = ['2024-2025', '2025-2026', '2026-2027'];

  useEffect(() => {
    fetchFees();
    fetchClassrooms();
  }, [page, filterPeriodicity, filterYear]);

  const fetchFees = async () => {
    try {
      setLoading(true);
      const response = await schoolFeesApi.getAll({
        page,
        limit: 10,
        periodicity: filterPeriodicity || undefined,
        schoolYear: filterYear || undefined,
      });
      setFees(response.data);
      setTotalPages(response.totalPages);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des frais');
    } finally {
      setLoading(false);
    }
  };

  const fetchClassrooms = async () => {
    try {
      const response = await classroomsApi.getAll({ limit: 100 });
      setClassrooms(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des classes:', err);
    }
  };

  const handleOpenDialog = (fee?: SchoolFee) => {
    if (fee) {
      setEditingFee(fee);
      setFormData({
        label: fee.label,
        amount: fee.amount.toString(),
        periodicity: fee.periodicity,
        schoolYear: fee.schoolYear,
        currency: fee.currency || 'CDF',
        classroomIds: fee.classroomIds?.map(c => typeof c === 'string' ? c : c._id) || [],
      });
    } else {
      setEditingFee(null);
      setFormData({
        label: '',
        amount: '',
        periodicity: 'mensuel',
        schoolYear: schoolYears[1],
        currency: 'CDF',
        classroomIds: [],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingFee(null);
  };

  const handleSubmit = async () => {
    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
      };
      
      if (editingFee) {
        await schoolFeesApi.update(editingFee._id, data);
      } else {
        await schoolFeesApi.create(data);
      }
      handleCloseDialog();
      fetchFees();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDeleteConfirm = async () => {
    if (feeToDelete) {
      try {
        await schoolFeesApi.delete(feeToDelete);
        setDeleteDialogOpen(false);
        setFeeToDelete(null);
        fetchFees();
      } catch (err: any) {
        setError(err.message || 'Erreur lors de la suppression');
        setDeleteDialogOpen(false);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setFeeToDelete(null);
  };

  const getPeriodicityLabel = (periodicity: string) => {
    const p = periodicities.find(t => t.value === periodicity);
    return p?.label || periodicity;
  };

  const handleToggleActive = async (fee: SchoolFee) => {
    try {
      await schoolFeesApi.toggleActive(fee._id);
      fetchFees();
    } catch (err: any) {
      setError(err.message || 'Erreur lors du changement de statut');
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#0D3C4C', mb: 1 }}>
            {currentText.title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {currentText.description}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Plus size={20} />}
          onClick={() => handleOpenDialog()}
          sx={{ 
            backgroundColor: '#0D3C4C',
            '&:hover': { backgroundColor: '#1A5F7A' }
          }}
        >
          {currentText.addFee}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>{currentText.filterPeriodicity}</InputLabel>
            <Select
              value={filterPeriodicity}
              onChange={(e) => setFilterPeriodicity(e.target.value)}
              label={currentText.filterPeriodicity}
            >
              <MenuItem value="">{currentText.all}</MenuItem>
              {periodicities.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>{currentText.filterYear}</InputLabel>
            <Select
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              label={currentText.filterYear}
            >
              <MenuItem value="">{currentText.all}</MenuItem>
              {schoolYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.label}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.periodicity}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.amount}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.schoolYear}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.classrooms}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.status}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.actions}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }}>{currentText.loading}</Typography>
                </TableCell>
              </TableRow>
            ) : fees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">{currentText.noFees}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              fees.map((fee) => (
                <TableRow key={fee._id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{fee.label}</TableCell>
                  <TableCell>
                    <Chip label={getPeriodicityLabel(fee.periodicity)} size="small" color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    {fee.amount.toLocaleString()} {fee.currency || 'CDF'}
                  </TableCell>
                  <TableCell>{fee.schoolYear}</TableCell>
                  <TableCell>
                    <Chip label={fee.classroomIds?.length || 0} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={fee.active ? currentText.active : currentText.inactive}
                      color={fee.active ? 'success' : 'default'}
                      size="small"
                      onClick={() => handleToggleActive(fee)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(fee)}
                      sx={{ color: '#0D3C4C' }}
                    >
                      <Edit size={18} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleActive(fee)}
                      sx={{ color: fee.active ? '#2e7d32' : '#9e9e9e' }}
                      title={fee.active ? currentText.inactive : currentText.active}
                    >
                      {fee.active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingFee ? currentText.editFee : currentText.addFee}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
            <TextField
              label={currentText.label}
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label={currentText.amount}
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
              fullWidth
            />
            <FormControl fullWidth required>
              <InputLabel>{currentText.periodicity}</InputLabel>
              <Select
                value={formData.periodicity}
                onChange={(e) => setFormData({ ...formData, periodicity: e.target.value as any })}
                label={currentText.periodicity}
              >
                {periodicities.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>{currentText.schoolYear}</InputLabel>
              <Select
                value={formData.schoolYear}
                onChange={(e) => setFormData({ ...formData, schoolYear: e.target.value })}
                label={currentText.schoolYear}
              >
                {schoolYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{currentText.currency}</InputLabel>
              <Select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                label={currentText.currency}
              >
                <MenuItem value="CDF">CDF (Franc Congolais)</MenuItem>
                <MenuItem value="USD">USD (Dollar Américain)</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ gridColumn: '1 / -1' }}>
              <InputLabel>{currentText.classrooms}</InputLabel>
              <Select
                multiple
                value={formData.classroomIds}
                onChange={(e) => setFormData({ ...formData, classroomIds: e.target.value as string[] })}
                label={currentText.classrooms}
              >
                {classrooms.map((classroom) => (
                  <MenuItem key={classroom._id} value={classroom._id}>
                    {classroom.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{currentText.cancel}</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{ 
              backgroundColor: '#0D3C4C',
              '&:hover': { backgroundColor: '#1A5F7A' }
            }}
          >
            {currentText.save}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title={language === 'fr' ? 'Supprimer le frais scolaire' : 'Delete School Fee'}
        message={language === 'fr' 
          ? 'Êtes-vous sûr de vouloir supprimer ce frais scolaire ? Cette action est irréversible.'
          : 'Are you sure you want to delete this school fee? This action is irreversible.'}
        confirmText={language === 'fr' ? 'Supprimer' : 'Delete'}
        cancelText={currentText.cancel}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        type="danger"
      />
    </Container>
  );
};

export default SchoolFees;
